import { useState, useEffect, type CSSProperties } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Check, Lock, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useEntitlements } from '@/entitlements/useEntitlements';
import { toast } from '@/hooks/use-toast';
import clsx from 'clsx';
import { logger } from '@/lib/logger';

interface PricingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const MONTHLY_PRICE_ID = import.meta.env.VITE_STRIPE_PRICE_MONTHLY || 'price_1Spkpc859kc9d4WErrjgwsOo';
const ANNUAL_PRICE_ID  = import.meta.env.VITE_STRIPE_PRICE_ANNUAL  || 'price_1Spl6p859kc9d4WEtEDcjkwJ';

// Helper gradient styles
const grad = 'linear-gradient(to bottom, #70BAFF, #258BFF)';

const gradientText: CSSProperties = {
  backgroundImage: grad,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  color: 'transparent',
};

const gradientUnderline: CSSProperties = {
  textDecoration: 'none',      // we’ll draw a custom underline
  backgroundImage: grad,       // the underline gradient
  backgroundRepeat: 'no-repeat',
  backgroundSize: '100% 1px',  // thickness
  backgroundPosition: '0 100%',// place at the bottom
  display: 'inline-block',
  paddingBottom: 1,            // room for the underline
};

export const PricingModal = ({ open, onOpenChange }: PricingModalProps) => {
  const [plan, setPlan] = useState<'monthly' | 'annual'>('monthly');
  const [isLoading, setIsLoading] = useState(false);
  const [couponOpen, setCouponOpen] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const { isAuthenticated, isSubscribed, entitlementLoaded } = useEntitlements();
  const location = useLocation();

  useEffect(() => {
    if (entitlementLoaded && isAuthenticated && isSubscribed) onOpenChange(false);
  }, [entitlementLoaded, isAuthenticated, isSubscribed, onOpenChange]);

  const monthlyPrice = 14.95;   // €/mo
  const annualTotal  = 125.58;  // €/yr
  const annualMoEq   = 10.47;   // €/mo eq
  const annualSave   = 53.82;   // € saved vs 12×monthly

  const priceId = plan === 'annual' ? ANNUAL_PRICE_ID : MONTHLY_PRICE_ID;
  const formatEuro = (n: number) => n.toFixed(2).replace('.', ',');

  async function handleStartTrial() {
    if (!isAuthenticated) {
      onOpenChange(false);
      const next = encodeURIComponent(location.pathname + location.search);
      window.location.href = `/auth/signin?next=${next}`;
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: {
          priceId,
          trialDays: 7,
          couponCode: couponCode.trim() || undefined,
          baseUrl: window.location.origin, // <— tell the function where to send the user back
        },
      });
      if (error) throw new Error(error.message || 'Failed to create checkout session');
      if (!data?.url) throw new Error('No checkout URL returned');

      window.location.href = data.url; // same-tab redirect
    } catch (err: any) {
      logger.error('Checkout error', err, {
        priceId,
        trialDays: 7,
        couponCode: couponCode.trim() || undefined,
      });
      toast({
        title: 'Unable to start checkout',
        description: err?.message || 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }

  const features = [
    '500+ winning ads',
    '50+ viral DTC brands',
    'Updated weekly with new ads',
    'Q1 Board coming',
    'More features coming',
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[660px] p-0 overflow-hidden border-0 shadow-xl bg-[#13171c]/95 backdrop-blur-xl border-white/10">
        {/* Soft top gradient + "Pricing" pill */}
        <div className="relative">
          <div className="h-28" />
          <div className="absolute left-1/2 top-6 -translate-x-1/2">
            <span
              className="px-6 py-2 rounded-full text-white text-[10px] font-bold uppercase tracking-widest backdrop-blur border border-white/20 shadow-lg shadow-[#70BAFF]/20"
              style={{ background: "linear-gradient(180deg, #70BAFF 0%, #258BFF 100%)" }}
            >
              Pricing plans
            </span>
          </div>
        </div>

        {/* Main card */}
        <div className="px-6 pb-8 -mt-10">
          <Card className="mx-auto w-full max-w-[500px] bg-white/5 rounded-2xl border border-white/10 shadow-[0_10px_40px_rgba(0,0,0,0.3)]">
            <CardHeader className="pt-8">
              {/* Segmented plan toggle */}
              <div className="mx-auto mb-6 flex w-fit items-center rounded-full border border-white/10 bg-white/5 p-1 shadow-sm">
                <button
                  type="button"
                  onClick={() => setPlan('monthly')}
                  className={clsx(
                    'px-4 py-1.5 text-sm rounded-full transition',
                    plan === 'monthly'
                      ? 'bg-white text-[#13171c] shadow'
                      : 'text-white/70 hover:bg-white/10'
                  )}
                  aria-pressed={plan === 'monthly'}
                >
                  Monthly
                </button>
                <button
                  type="button"
                  onClick={() => setPlan('annual')}
                  className={clsx(
                    'px-4 py-1.5 text-sm rounded-full transition flex items-center gap-2',
                    plan === 'annual'
                      ? 'bg-white text-[#13171c] shadow'
                      : 'text-white/70 hover:bg-white/10'
                  )}
                  aria-pressed={plan === 'annual'}
                >
                  Annual <span className="text-xs opacity-80">(save ~30%)</span>
                </button>
              </div>

              <CardTitle className="text-center text-4xl font-bold text-white capitalize tracking-tight">
                {plan === 'annual' ? 'Annual plan' : 'Monthly plan'}
              </CardTitle>
              <CardDescription className="text-center text-white/60 font-medium">
                Full access to all premium features
              </CardDescription>

              {/* Price block (euro comma style) */}
              <div className="mt-4 text-center">
                {plan === 'annual' ? (
                  <>
                    <div className="text-4xl sm:text-5xl font-bold tracking-tight text-white">
                      €{formatEuro(annualMoEq)}
                      <span className="text-lg text-white/50 font-normal">/month</span>
                    </div>
                    <div className="text-sm text-white/50 mt-1">
                      billed annually €{formatEuro(annualTotal)}/year (save €{formatEuro(annualSave)})
                    </div>
                  </>
                ) : (
                  <div className="text-4xl sm:text-5xl font-bold tracking-tight text-white">
                    €{formatEuro(monthlyPrice)}
                    <span className="text-lg text-white/50 font-normal">/month</span>
                  </div>
                )}
              </div>
            </CardHeader>

            <CardContent className="pb-8">
              {/* Feature checklist */}
              <ul className="space-y-4 mb-8">
                {features.map((f) => (
                  <li key={f} className="flex items-center gap-4 group">
                    <div className="h-6 w-6 rounded-full bg-[#70BAFF]/10 flex items-center justify-center shrink-0 border border-[#70BAFF]/20 group-hover:scale-110 transition-transform">
                      <Check className="h-3.5 w-3.5 shrink-0 text-[#70BAFF]" />
                    </div>
                    <span className="text-sm text-white/80 font-medium">{f}</span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <button
                onClick={handleStartTrial}
                disabled={isLoading}
                className="btn-glow w-full px-8 py-4 text-sm font-bold uppercase tracking-widest text-white rounded-xl shadow-lg hover:opacity-90 active:scale-[0.98] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                style={{
                  background: 'linear-gradient(to bottom, #70BAFF, #258BFF)',
                }}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Redirecting…
                  </>
                ) : (
                  <>
                    🚀 Start your 7-day <span className="font-extrabold">free trial</span>
                  </>
                )}
              </button>

              {/* Trust row: gradient only on "today" */}
              <p className="mt-3 flex items-center justify-center gap-2 text-[12px] text-white/50">
                <Lock className="h-3.5 w-3.5" />
                <span className="font-bold">No charge </span>
                <span className="font-bold" style={gradientText}>today</span>
                <span> • Secure checkout by Stripe</span>
              </p>

              {/* Fine print with gradient underline + gradient link text */}
              <p className="mt-2 text-center text-[11px] leading-relaxed text-white/40">
                Trial starts today. You won't be charged until after <span className="font-medium">7</span> days.
                {' '}Cancel anytime during your trial. By continuing you agree to our{' '}
                <a href="/terms" target="_blank" rel="noreferrer" style={gradientUnderline}>
                  <span style={gradientText}>Terms of Service</span>
                </a>{' '}and{' '}
                <a href="/privacy" target="_blank" rel="noreferrer" style={gradientUnderline}>
                  <span style={gradientText}>Privacy Policy</span>
                </a>.
              </p>

              {/* a11y live region */}
              <div className="sr-only" aria-live="polite" aria-atomic="true">
                {isLoading ? 'Loading' : ''}
              </div>
            </CardContent>
          </Card>

          {/* Brand logo */}
          <div className="mt-6 text-center">
            <img
              src="/lovable-uploads/obyect_new.svg"
              alt="Obyect"
              className="mx-auto h-6 w-auto opacity-60"
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
